import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { loadWorld } from '../utils/worldLoader';
import { buildWorldDocs } from '../utils/worldSearchDocs';
import { registerWorldRoutes } from '../utils/prettyRoutes';
import { monsterKind } from '../utils/monsterKind';
import type { SearchDoc } from '../utils/guideSearch';
import type { WorldData } from '../types/world';
type WorldState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data?: WorldData;
  docs: SearchDoc[];
  error?: string;
};
type Ctx = WorldState & {
  ensure: () => void;
};
const WorldContext = createContext<Ctx | null>(null);

/** Carga los CSV una sola vez, solo cuando una página de la guía los necesita. */
export function WorldDataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<WorldState>({
    status: 'idle',
    docs: []
  });
  const started = useRef(false);
  const ensure = useCallback(() => {
    if (started.current) return;
    started.current = true;
    setState({
      status: 'loading',
      docs: []
    });
    loadWorld().then(data => {
      // Rutas legibles antes de construir el buscador y renderizar enlaces.
      registerWorldRoutes(data, m => monsterKind(data, m) === 'npc');
      setState({
        status: 'ready',
        data,
        docs: buildWorldDocs(data)
      });
    }).catch((e: unknown) => {
      started.current = false;
      setState({
        status: 'error',
        docs: [],
        error: e instanceof Error ? e.message : String(e)
      });
    });
  }, []);
  const value = useMemo(() => ({
    ...state,
    ensure
  }), [state, ensure]);
  return <WorldContext.Provider value={value} data-id="element-2109" data-mp="3jqf8it5bvzo7">{children}</WorldContext.Provider>;
}
export function useWorld() {
  const ctx = useContext(WorldContext);
  if (!ctx) throw new Error('useWorld debe usarse dentro de WorldDataProvider');
  const {
    ensure
  } = ctx;
  useEffect(() => ensure(), [ensure]);
  return ctx;
}